import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Chip,
  Tabs,
  Tab,
  Pagination,
  useTheme,
  useMediaQuery,
  CircularProgress,
  TextField,
  InputAdornment,
} from "@mui/material";
import { Search } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import * as Constant from "../features/constant";
import { useGetBlogListQuery } from "../features/api/blogApiSlice";

const CATEGORIES = [
  { code: null, labelKey: "blog.all", fallback: "All" },
  { code: "ESPORTS", labelKey: "blog.esports", fallback: "Esports" },
  { code: "GUIDELINE", labelKey: "blog.guideline", fallback: "Guideline" },
  { code: "CARD", labelKey: "blog.card", fallback: "Card" },
  { code: "SLOT_GAMES", labelKey: "blog.slotgames", fallback: "Slot Games" },
  { code: "CRASH_GAMES", labelKey: "blog.crashgames", fallback: "Crash Games" },
  { code: "VIP", labelKey: "blog.vip", fallback: "VIP" },
];

const categoryColors = {
  ESPORTS: "#3b82f6",
  GUIDELINE: "#22c55e",
  CARD: "#f97316",
  SLOT_GAMES: "#a855f7",
  CRASH_GAMES: "#ef4444",
  VIP: "#eab308",
};

const Blog = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [selectedCategory, setSelectedCategory] = useState(null);
  const [page, setPage] = useState(0);
  const [searchText, setSearchText] = useState("");

  const { data: blogData, isLoading } = useGetBlogListQuery({
    category: selectedCategory,
    page,
    pageSize: 12,
  });

  const blogs = blogData?.result?.content || [];
  const totalPages = blogData?.result?.totalPages || 0;

  const filteredBlogs = searchText
    ? blogs.filter(
        (b) =>
          b.title?.toLowerCase().includes(searchText.toLowerCase()) ||
          b.description?.toLowerCase().includes(searchText.toLowerCase())
      )
    : blogs;

  const handleCategoryChange = (_, newValue) => {
    setSelectedCategory(CATEGORIES[newValue].code);
    setPage(0);
  };

  const handlePageChange = (_, newPage) => {
    setPage(newPage - 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    try {
      return new Date(dateStr).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch {
      return dateStr;
    }
  };

  const selectedTabIndex = CATEGORIES.findIndex((c) => c.code === selectedCategory);

  return (
    <Box
      sx={{
        backgroundColor: theme.palette.background.secondaryPaper,
        minHeight: "80vh",
        pt: isMobile ? 8 : 3,
        pb: 4,
      }}
    >
      <Container maxWidth="lg">
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: isMobile ? "flex-start" : "center",
            flexDirection: isMobile ? "column" : "row",
            mb: 3,
            gap: 2,
          }}
        >
          <Typography
            variant="h5"
            sx={{
              color: process.env.REACT_APP_COLOR_TEXT || "white",
              fontWeight: 700,
            }}
          >
            {t("blog.title", "Blog")}
          </Typography>
          <TextField
            size="small"
            placeholder={t("blog.search", "Search articles...")}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: "grey.500" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: isMobile ? "100%" : 280,
              "& .MuiOutlinedInput-root": {
                color: process.env.REACT_APP_COLOR_TEXT || "white",
                "& fieldset": { borderColor: "rgba(255,255,255,0.2)" },
                "&:hover fieldset": { borderColor: "rgba(255,255,255,0.4)" },
              },
            }}
          />
        </Box>

        {/* Category tabs */}
        <Tabs
          value={selectedTabIndex >= 0 ? selectedTabIndex : 0}
          onChange={handleCategoryChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            mb: 3,
            "& .MuiTab-root": {
              color: process.env.REACT_APP_COLOR_TEXT || "white",
              textTransform: "none",
              fontSize: "14px",
              minWidth: "auto",
              px: 2,
            },
            "& .Mui-selected": {
              color: `${theme.palette.primary.main} !important`,
              fontWeight: 600,
            },
            "& .MuiTabs-indicator": {
              backgroundColor: theme.palette.primary.main,
            },
          }}
        >
          {CATEGORIES.map((cat) => (
            <Tab key={cat.code || "all"} label={t(cat.labelKey, cat.fallback)} />
          ))}
        </Tabs>

        {/* Blog cards */}
        {isLoading ? (
          <Box display="flex" justifyContent="center" py={8}>
            <CircularProgress />
          </Box>
        ) : filteredBlogs.length === 0 ? (
          <Box py={8} textAlign="center">
            <Typography
              variant="body1"
              sx={{ color: process.env.REACT_APP_COLOR_TEXT || "white", opacity: 0.6 }}
            >
              {t("blog.empty", "No articles found.")}
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={3}>
            {filteredBlogs.map((blog) => (
              <Grid item xs={12} sm={6} md={4} key={blog.id}>
                <Card
                  onClick={() => navigate(Constant.getPath(`/blog/${blog.slug}`))}
                  sx={{
                    cursor: "pointer",
                    backgroundColor: theme.palette.background.paper,
                    borderRadius: 2,
                    overflow: "hidden",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    transition: "transform 0.2s, box-shadow 0.2s",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: "0 8px 24px rgba(0,0,0,0.3)",
                    },
                  }}
                >
                  <Box sx={{ position: "relative" }}>
                    <CardMedia
                      component="img"
                      height={180}
                      image={blog.thumbnailImageUrl || blog.bannerImageUrl || ""}
                      alt={blog.title}
                      sx={{ objectFit: "cover" }}
                    />
                    <Chip
                      label={blog.category?.replace(/_/g, " ")}
                      size="small"
                      sx={{
                        position: "absolute",
                        top: 12,
                        left: 12,
                        backgroundColor: categoryColors[blog.category] || "#666",
                        color: "white",
                        fontWeight: 600,
                        fontSize: "11px",
                      }}
                    />
                  </Box>
                  <CardContent sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: process.env.REACT_APP_COLOR_TEXT || "white",
                        opacity: 0.5,
                        mb: 0.5,
                      }}
                    >
                      {blog.author && `${blog.author} · `}
                      {formatDate(blog.publishedAt)}
                    </Typography>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        color: process.env.REACT_APP_COLOR_TEXT || "white",
                        fontWeight: 600,
                        lineHeight: 1.3,
                        mb: 1,
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {blog.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: process.env.REACT_APP_COLOR_TEXT || "white",
                        opacity: 0.6,
                        lineHeight: 1.5,
                        flex: 1,
                        display: "-webkit-box",
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {blog.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <Box display="flex" justifyContent="center" mt={4}>
            <Pagination
              count={totalPages}
              page={page + 1}
              onChange={handlePageChange}
              color="primary"
              sx={{
                "& .MuiPaginationItem-root": {
                  color: process.env.REACT_APP_COLOR_TEXT || "white",
                },
              }}
            />
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default Blog;
